import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Select,
  Switch,
  Button,
  Typography,
  Space,
  Alert,
  Divider,
  Spin,
  Input,
  Tooltip,
  Row,
  Col,
} from "antd";
import MDEditor from "@uiw/react-md-editor";
import "../../../../../../node_modules/@uiw/react-md-editor/markdown-editor.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { masterAdminApi } from "@api/index";

const { Title, Text } = Typography;

type Environment = "staging" | "production";
type BannerType = "warning" | "info" | "error";

interface BannerConfig {
  content: string;
  title?: string;
  banner_type?: BannerType;
  bg_color?: string;
  border_color?: string;
  text_color?: string;
}

const PRESETS: Record<
  BannerType,
  { bg: string; border: string; text: string }
> = {
  warning: { bg: "#fffbe6", border: "#ffe58f", text: "#7c5c00" },
  info: { bg: "#e6f7ff", border: "#91d5ff", text: "#003a6b" },
  error: { bg: "#fff1f0", border: "#ffa39e", text: "#820014" },
};

const DEFAULT_CONTENT = `At this time we are undergoing scheduled maintenance to resolve minor issues and improve performance. Service will resume shortly. For immediate support contact admin@janusadvisoryinc.com.`;

const SVG_STYLE: React.CSSProperties = { display: "block", flexShrink: 0 };
const ICONS: Record<BannerType, React.ReactNode> = {
  warning: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={SVG_STYLE}
    >
      <path
        d="M8 1.5L14.5 13H1.5L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M8 6V9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11" r="0.7" fill="currentColor" />
    </svg>
  ),
  info: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={SVG_STYLE}
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1.4"
        fill="none"
      />
      <path
        d="M8 7v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="5" r="0.75" fill="currentColor" />
    </svg>
  ),
  error: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={SVG_STYLE}
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1.4"
        fill="none"
      />
      <path
        d="M5.5 5.5l5 5M10.5 5.5l-5 5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
};
const DEFAULT_TITLE =
  "Climate Finance Intelligence is currently undergoing maintenance";

const ColorField: React.FC<{
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
}> = ({ label, value, placeholder, onChange }) => (
  <Space align="center">
    <Text strong style={{ minWidth: 100 }}>
      {label}:
    </Text>
    <Tooltip title="Click to pick a color">
      <input
        type="color"
        value={value || placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 36,
          height: 32,
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          cursor: "pointer",
          padding: 2,
        }}
      />
    </Tooltip>
    <Input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: 110, fontFamily: "monospace" }}
    />
    {value && (
      <Button size="small" type="link" onClick={() => onChange("")}>
        Reset
      </Button>
    )}
  </Space>
);

const AppSettings: React.FC = () => {
  const [environment, setEnvironment] = useState<Environment>("staging");
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  const [content, setContent] = useState<string>(DEFAULT_CONTENT);
  const [bannerType, setBannerType] = useState<BannerType>("warning");
  const [bgColor, setBgColor] = useState<string>("");
  const [borderColor, setBorderColor] = useState<string>("");
  const [textColor, setTextColor] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<"success" | "error" | null>(
    null,
  );

  const preset = PRESETS[bannerType];
  const previewBg = bgColor || preset.bg;
  const previewBorder = borderColor || preset.border;
  const previewText = textColor || preset.text;

  const fetchConfig = useCallback(async (env: Environment) => {
    setLoading(true);
    setSaveStatus(null);
    try {
      const res = await masterAdminApi.getAppConfig("maintenance_banner", env);
      if (res?.config) {
        const cfg = res.config;
        setIsActive(cfg.is_active ?? false);
        if (cfg.config_value) {
          try {
            const parsed: BannerConfig = JSON.parse(cfg.config_value);
            setContent(parsed.content ?? DEFAULT_CONTENT);
            setTitle(parsed.title ?? DEFAULT_TITLE);
            setBannerType(parsed.banner_type ?? "warning");
            setBgColor(parsed.bg_color ?? "");
            setBorderColor(parsed.border_color ?? "");
            setTextColor(parsed.text_color ?? "");
          } catch {
            setContent(cfg.config_value);
          }
        }
      } else {
        setContent(DEFAULT_CONTENT);
        setTitle(DEFAULT_TITLE);
        setBannerType("warning");
        setBgColor("");
        setBorderColor("");
        setTextColor("");
        setIsActive(false);
      }
    } catch (e) {
      console.error("Failed to fetch app config:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig(environment);
  }, [environment, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    const adminEmail = localStorage.getItem("master_email") ?? "admin";
    const configValue: BannerConfig = {
      content,
      title: title || undefined,
      banner_type: bannerType,
      bg_color: bgColor || undefined,
      border_color: borderColor || undefined,
      text_color: textColor || undefined,
    };
    try {
      await masterAdminApi.upsertAppConfig({
        config_key: "maintenance_banner",
        config_value: JSON.stringify(configValue),
        environment,
        is_active: isActive,
        updated_by: adminEmail,
      });
      setSaveStatus("success");
    } catch (e) {
      console.error("Failed to save app config:", e);
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: 1100 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        App Settings
      </Title>
      <Text type="secondary">
        Configure application-wide settings such as maintenance banners.
      </Text>

      <Divider />

      <Card title="Maintenance Banner" style={{ marginTop: 16 }}>
        {loading ? (
          <Spin />
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {/* Row 1: env + enabled */}
            <Row gutter={32} align="middle">
              <Col>
                <Space align="center">
                  <Text strong>Environment:</Text>
                  <Select<Environment>
                    value={environment}
                    onChange={setEnvironment}
                    style={{ width: 160 }}
                    options={[
                      { value: "staging", label: "Staging" },
                      { value: "production", label: "Production" },
                    ]}
                  />
                </Space>
              </Col>
              <Col>
                <Space align="center">
                  <Text strong>Enabled:</Text>
                  <Switch checked={isActive} onChange={setIsActive} />
                  <Text type="secondary">
                    {isActive ? "Live on this environment" : "Hidden"}
                  </Text>
                </Space>
              </Col>
            </Row>

            {/* Row 2: type + colors */}
            <Card
              size="small"
              title="Appearance"
              style={{ background: "#fafafa" }}
            >
              <Space direction="vertical" size="middle">
                <Space align="center">
                  <Text strong style={{ minWidth: 100 }}>
                    Base type:
                  </Text>
                  <Select<BannerType>
                    value={bannerType}
                    onChange={(val) => {
                      setBannerType(val);
                      // Reset custom colors so preview updates to new preset
                      setBgColor("");
                      setBorderColor("");
                      setTextColor("");
                    }}
                    style={{ width: 140 }}
                    options={[
                      { value: "warning", label: "⚠ Warning" },
                      { value: "info", label: "ℹ Info" },
                      { value: "error", label: "✕ Error" },
                    ]}
                  />
                  <Text type="secondary">
                    Sets default colors — override below if needed
                  </Text>
                </Space>
                <ColorField
                  label="Background"
                  value={bgColor}
                  placeholder={preset.bg}
                  onChange={setBgColor}
                />
                <ColorField
                  label="Border"
                  value={borderColor}
                  placeholder={preset.border}
                  onChange={setBorderColor}
                />
                <ColorField
                  label="Text"
                  value={textColor}
                  placeholder={preset.text}
                  onChange={setTextColor}
                />
              </Space>
            </Card>

            {/* Title */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Title{" "}
                <Text type="secondary">
                  (optional, shown in bold above the body)
                </Text>
                :
              </Text>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Climate Finance Intelligence is currently undergoing maintenance"
                allowClear
              />
            </div>

            {/* Markdown editor */}
            <div data-color-mode="light">
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Body content <Text type="secondary">(Markdown)</Text>:
              </Text>
              <MDEditor
                value={content}
                onChange={(val) => setContent(val ?? "")}
                height={260}
                preview="edit"
              />
            </div>

            {/* Live preview */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Live preview:
              </Text>
              <div
                style={{
                  background: previewBg,
                  border: `1px solid ${previewBorder}`,
                  borderRadius: 6,
                  color: previewText,
                  padding: "12px 16px",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {title ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginBottom: 4,
                      }}
                    >
                      <span style={{ color: previewText, lineHeight: 0 }}>
                        {ICONS[bannerType]}
                      </span>
                      <div style={{ fontWeight: 600 }}>{title}</div>
                    </div>
                    <div
                      className="banner-markdown-preview"
                      style={{ color: previewText, paddingLeft: 24 }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {content}
                      </ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        color: previewText,
                        lineHeight: 0,
                        marginTop: 2,
                      }}
                    >
                      {ICONS[bannerType]}
                    </span>
                    <div
                      className="banner-markdown-preview"
                      style={{ color: previewText }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {content}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {saveStatus === "success" && (
              <Alert
                type="success"
                message={`Banner for ${environment} saved successfully.`}
                showIcon
                closable
                afterClose={() => setSaveStatus(null)}
              />
            )}
            {saveStatus === "error" && (
              <Alert
                type="error"
                message="Failed to save banner. Please try again."
                showIcon
                closable
                afterClose={() => setSaveStatus(null)}
              />
            )}

            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Banner
            </Button>
          </Space>
        )}
      </Card>
    </div>
  );
};

export default AppSettings;
